"use client"

import { useLayoutEffect } from "react"
import { usePathname } from "next/navigation"

/** Deterministic scroll-to-top on every marketing navigation.
 *
 *  Next's own scroll reset anchors on the destination segment's first
 *  element, and when the elements at the top of a page have no height it
 *  silently does nothing: clicking the footer's Complete Dating System link
 *  from another page changed the URL while the viewport stayed at the old
 *  offset (owner, twice: "not reloading to top of page from the footer
 *  link"). Proven live against production before this fix: /mentorship to
 *  /complete-dating-system kept scrollY at 16663, and /articles failed the
 *  same way, while /compare and /mentorship happened to work. So the reset
 *  is done here explicitly on every pathname change.
 *
 *  Two deliberate exceptions: a hash navigation keeps Next's anchor scroll,
 *  and a back/forward navigation keeps the browser's restored position (the
 *  popstate flag below is set before Next re-renders, so the effect skips
 *  exactly those). Same-page footer clicks never change the pathname, so
 *  they stay with FooterLink's own smooth-scroll handler.
 */
let cameFromHistory = false
if (typeof window !== "undefined") {
  window.addEventListener("popstate", () => {
    cameFromHistory = true
  })
}

export function ScrollToTop() {
  const pathname = usePathname()
  useLayoutEffect(() => {
    if (cameFromHistory) {
      cameFromHistory = false
      return
    }
    if (window.location.hash) return
    window.scrollTo(0, 0)
    // Next is not done: on a page that streams through a Suspense boundary
    // (/articles is one) its ScrollAndFocusHandler runs AFTER this effect,
    // when the boundary resolves, and scrollIntoViews whichever element had
    // height at that instant - measured landing the viewport 1147px down.
    // So the reset holds for a short window: any scroll that is not the
    // reader's own is put back to the top. The first real wheel, touch, key
    // or mouse press disarms it, so a fast reader is never fought.
    let readerMoved = false
    const disarm = () => {
      readerMoved = true
    }
    const inputs = ["wheel", "touchstart", "keydown", "mousedown"] as const
    for (const e of inputs) window.addEventListener(e, disarm, { passive: true })
    const onScroll = () => {
      if (!readerMoved && window.scrollY !== 0) window.scrollTo(0, 0)
    }
    window.addEventListener("scroll", onScroll, { passive: true })
    const cleanup = () => {
      window.removeEventListener("scroll", onScroll)
      for (const e of inputs) window.removeEventListener(e, disarm)
    }
    const stop = setTimeout(cleanup, 1200)
    return () => {
      clearTimeout(stop)
      cleanup()
    }
  }, [pathname])
  return null
}
